/**
 * Family/Friends Pty company registration — the ONE set of rules.
 *
 * Imported by the website wizard (inline errors per step) AND by the
 * submitCompanyRegistration Cloud Function (re-validates everything before a
 * single byte is written), so client and server can never disagree.
 *
 * Dependency-free on purpose: it compiles under the website's strict
 * tsconfig and the functions' es2017 target alike.
 *
 * Errors are returned as { [fieldPath]: message } with paths like
 * `company.authorisedShares`, `people.2.idNumber`, `people.shareholdingTotal`,
 * `name.options.1`, `declaration.signature` — the wizard maps each path to
 * the input it belongs to; the function returns the same map to the client.
 */

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;
export type MonthName = (typeof MONTHS)[number];

export type IdDocType = 'green_book' | 'smart_card';

export const ALLOWED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf'] as const;
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** Soft warning (not a block) above this many people (brief: warn above 10). */
export const PEOPLE_WARNING_THRESHOLD = 10;
/** Hard ceiling — abuse guard only; no real group comes near it. */
export const MAX_PEOPLE = 30;

export interface PersonRoles {
  director: boolean;
  shareholder: boolean;
  incorporator: boolean;
}

/**
 * Upload slots. On the client these hold a marker ('selected') once a file is
 * chosen / a signature drawn; on the server they hold the Storage paths the
 * draft handed out (company-registrations/<id>/uploads/...).
 */
export interface PersonUploads {
  idFront?: string;
  idBack?: string;
  signature?: string;
}

export interface RegistrationPerson {
  /** Stable client key (React list key); not trusted for anything. */
  key: string;
  fullName: string;
  idNumber: string;
  cellphone: string;
  email: string;
  homeAddress: string;
  roles: PersonRoles;
  /** Shareholders only. */
  shares: number | null;
  /** Shareholders only, 0–100, max 2 decimals. */
  shareholdingPct: number | null;
  idDocType: IdDocType | '';
  creditCheckConsent: boolean;
  /** POA "Signed at" place. */
  signedAtPlace: string;
  uploads: PersonUploads;
}

export interface RegistrationCompany {
  yearEndMonth: MonthName | '';
  authorisedShares: number | null;
  businessAddress: string;
  postalSameAsBusiness: boolean;
  postalAddress: string;
}

export interface RegistrationName {
  hasReservation: 'yes' | 'no' | '';
  reservedName: string;
  reservationReference: string;
  options: [string, string, string, string];
}

export interface RegistrationDeclaration {
  accepted: boolean;
  fullName: string;
  signature?: string;
}

export interface RegistrationPayload {
  company: RegistrationCompany;
  people: RegistrationPerson[];
  name: RegistrationName;
  declaration: RegistrationDeclaration;
}

export type FieldErrors = Record<string, string>;

// ── Wording shared by the form and the PDF ───────────────────────────────

export const DECLARATION_TEXT =
  'I confirm that the information provided above is true and correct to the best of my knowledge.';

export const POA_TITLE = 'POWER OF ATTORNEY FOR REQUESTING AND OBTAINING PERSONAL CREDIT RECORDS';

/** The POA body for one consumer, with their name and ID filled in. */
export function poaParagraphs(fullName: string, idNumber: string): string[] {
  return [
    `I, the undersigned, ${fullName || '[Full name]'}, ID number ${idNumber || '[ID number]'},`,
    '1. Do hereby appoint PropInvest and ooba as my lawful representatives and agents to process my personal information to obtain a copy of my personal credit report ("PCR") from a registered credit bureau, including via a registered reseller provider. I consent to the credit bureau and/or other registered provider releasing a copy of my PCR to PropInvest and ooba to review solely for the purpose of providing me with advice or assistance with managing my credit.',
    '2. Attached to this Power of Attorney is a copy of my identity document.',
    'I confirm that a clear copy of my Identity Document / Identity Card (front and back) is attached.',
  ];
}

export const POA_FOOTER =
  'This Power of Attorney authorises PropInvest and ooba to obtain personal credit reports solely for the purpose of providing credit advice or assistance.';

// ── Field-level helpers ──────────────────────────────────────────────────

const trim = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export interface SaIdCheck {
  ok: boolean;
  error?: string;
  /** YYYY-MM-DD, when the date portion is a real date. */
  dateOfBirth?: string;
}

/**
 * South African ID number: 13 digits, Luhn check digit, YYMMDD a real date,
 * not in the future, holder 18 or older. Two-digit years above the current
 * year's are 19xx, the rest 20xx (so someone over 100 reads as a child —
 * accepted edge case).
 */
export function checkSaIdNumber(raw: string, today: Date = new Date()): SaIdCheck {
  const id = trim(raw).replace(/\s+/g, '');
  if (!id) return { ok: false, error: 'Enter the ID number.' };
  if (!/^\d{13}$/.test(id)) return { ok: false, error: 'An SA ID number has exactly 13 digits.' };

  const yy = Number(id.slice(0, 2));
  const mm = Number(id.slice(2, 4));
  const dd = Number(id.slice(4, 6));
  const currentYY = today.getUTCFullYear() % 100;
  const year = yy > currentYY ? 1900 + yy : 2000 + yy;
  const dob = new Date(Date.UTC(year, mm - 1, dd));
  const realDate = mm >= 1 && mm <= 12 && dob.getUTCFullYear() === year && dob.getUTCMonth() === mm - 1 && dob.getUTCDate() === dd;
  if (!realDate) return { ok: false, error: 'The date of birth in this ID number is not a real date.' };
  const dateOfBirth = `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;

  if (!luhnValid(id)) return { ok: false, error: 'This ID number is not valid. Please check the digits.', dateOfBirth };

  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  if (dob.getTime() > todayUtc) return { ok: false, error: 'The date of birth in this ID number is in the future.', dateOfBirth };
  const eighteenth = Date.UTC(year + 18, mm - 1, dd);
  if (eighteenth > todayUtc) return { ok: false, error: 'Everyone on the form must be 18 or older.', dateOfBirth };

  return { ok: true, dateOfBirth };
}

function luhnValid(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/**
 * SA cellphone → +27XXXXXXXXX. Accepts 0XX XXX XXXX, +27…, 27…, with spaces,
 * dashes or brackets. Mobile prefixes only (06x / 07x / 08x). Null if invalid.
 */
export function normaliseSaCellphone(raw: string): string | null {
  const digits = trim(raw).replace(/[\s\-()]/g, '');
  let national: string | null = null;
  if (/^\+27\d{9}$/.test(digits)) national = digits.slice(3);
  else if (/^27\d{9}$/.test(digits)) national = digits.slice(2);
  else if (/^0\d{9}$/.test(digits)) national = digits.slice(1);
  if (!national || !/^[678]\d{8}$/.test(national)) return null;
  return `+27${national}`;
}

export function isValidEmail(raw: string): boolean {
  const v = trim(raw);
  return v.length <= 200 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
}

/** Up to 2 decimals, 0–100. */
function isValidPct(v: unknown): v is number {
  return typeof v === 'number' && isFinite(v) && v > 0 && v <= 100 && Math.round(v * 100) === Number((v * 100).toFixed(6));
}

function isPositiveInt(v: unknown): v is number {
  return typeof v === 'number' && isFinite(v) && Number.isInteger(v) && v >= 1;
}

function tooLong(v: string, max: number): boolean {
  return v.length > max;
}

// ── Step validators ──────────────────────────────────────────────────────

export function validateCompanyStep(company: RegistrationCompany): FieldErrors {
  const e: FieldErrors = {};
  if (!company.yearEndMonth || MONTHS.indexOf(company.yearEndMonth as MonthName) < 0) {
    e['company.yearEndMonth'] = 'Choose the financial year-end month.';
  }
  if (!isPositiveInt(company.authorisedShares)) {
    e['company.authorisedShares'] = 'Enter a whole number of shares (at least 1).';
  } else if (company.authorisedShares > 1_000_000_000) {
    e['company.authorisedShares'] = 'That number of shares is too large.';
  }
  const business = trim(company.businessAddress);
  if (!business) e['company.businessAddress'] = 'Enter the business address.';
  else if (tooLong(business, 500)) e['company.businessAddress'] = 'Keep the address under 500 characters.';
  if (!company.postalSameAsBusiness) {
    const postal = trim(company.postalAddress);
    if (!postal) e['company.postalAddress'] = 'Enter the postal address, or tick "Same as business address".';
    else if (tooLong(postal, 500)) e['company.postalAddress'] = 'Keep the address under 500 characters.';
  }
  return e;
}

export interface PersonValidationOptions {
  today?: Date;
}

export function validatePerson(p: RegistrationPerson, index: number, opts: PersonValidationOptions = {}): FieldErrors {
  const e: FieldErrors = {};
  const at = (f: string) => `people.${index}.${f}`;

  const name = trim(p.fullName);
  if (!name) e[at('fullName')] = 'Enter the name and surname.';
  else if (name.split(/\s+/).length < 2) e[at('fullName')] = 'Enter both the name and the surname.';
  else if (tooLong(name, 120)) e[at('fullName')] = 'Keep the name under 120 characters.';

  const id = checkSaIdNumber(p.idNumber, opts.today);
  if (!id.ok) e[at('idNumber')] = id.error || 'Enter a valid SA ID number.';

  if (!trim(p.cellphone)) e[at('cellphone')] = 'Enter the cellphone number.';
  else if (!normaliseSaCellphone(p.cellphone)) e[at('cellphone')] = 'Enter a South African cellphone number, e.g. 082 123 4567.';

  if (!trim(p.email)) e[at('email')] = 'Enter the email address.';
  else if (!isValidEmail(p.email)) e[at('email')] = 'Enter a valid email address.';

  const home = trim(p.homeAddress);
  if (!home) e[at('homeAddress')] = 'Enter the home address.';
  else if (tooLong(home, 500)) e[at('homeAddress')] = 'Keep the address under 500 characters.';

  const roles = p.roles || { director: false, shareholder: false, incorporator: false };
  if (!roles.director && !roles.shareholder && !roles.incorporator) {
    e[at('roles')] = 'Tick at least one role.';
  }
  if (roles.shareholder) {
    if (!isPositiveInt(p.shares)) e[at('shares')] = 'Enter the number of shares (a whole number, at least 1).';
    if (!isValidPct(p.shareholdingPct)) e[at('shareholdingPct')] = 'Enter a shareholding between 0.01 and 100 (up to 2 decimals).';
  }

  if (p.idDocType !== 'green_book' && p.idDocType !== 'smart_card') {
    e[at('idDocType')] = 'Choose the ID document type.';
  }
  const up = p.uploads || {};
  if (!trim(up.idFront)) {
    e[at('uploads.idFront')] = p.idDocType === 'smart_card' ? 'Upload the front of the Smart ID card.' : 'Upload a copy of the ID document.';
  }
  if (p.idDocType === 'smart_card' && !trim(up.idBack)) {
    e[at('uploads.idBack')] = 'Upload the back of the Smart ID card.';
  }

  if (p.creditCheckConsent !== true) e[at('creditCheckConsent')] = 'The credit check authorisation is required for every person.';
  if (!trim(p.signedAtPlace)) e[at('signedAtPlace')] = 'Enter the place where the Power of Attorney is signed.';
  else if (tooLong(trim(p.signedAtPlace), 80)) e[at('signedAtPlace')] = 'Keep the place under 80 characters.';
  if (!trim(up.signature)) e[at('uploads.signature')] = 'Sign the Power of Attorney.';

  return e;
}

export interface ShareholdingSummary {
  totalPct: number;
  totalShares: number;
  shareholderCount: number;
}

export function shareholdingSummary(people: RegistrationPerson[]): ShareholdingSummary {
  let totalPct = 0;
  let totalShares = 0;
  let shareholderCount = 0;
  for (const p of people) {
    if (!p.roles || !p.roles.shareholder) continue;
    shareholderCount++;
    if (typeof p.shareholdingPct === 'number' && isFinite(p.shareholdingPct)) totalPct += p.shareholdingPct;
    if (typeof p.shares === 'number' && isFinite(p.shares)) totalShares += p.shares;
  }
  return { totalPct: Math.round(totalPct * 100) / 100, totalShares, shareholderCount };
}

/** Rules that span the whole People list (plus every person's own fields). */
export function validatePeopleStep(
  people: RegistrationPerson[],
  authorisedShares: number | null,
  opts: PersonValidationOptions = {}
): FieldErrors {
  const e: FieldErrors = {};
  if (!Array.isArray(people) || people.length === 0) {
    e['people'] = 'Add at least one person.';
    return e;
  }
  if (people.length > MAX_PEOPLE) {
    e['people'] = `A maximum of ${MAX_PEOPLE} people can be added.`;
    return e;
  }
  people.forEach((p, i) => Object.assign(e, validatePerson(p, i, opts)));

  if (!people.some((p) => p.roles && p.roles.director)) e['people.directors'] = 'At least one person must be a Director.';
  const incorporators = people.filter((p) => p.roles && p.roles.incorporator).length;
  if (incorporators === 0) e['people.incorporator'] = 'Exactly one person must be the Incorporator.';
  else if (incorporators > 1) e['people.incorporator'] = 'Only one person can be the Incorporator.';

  const seen: Record<string, number> = {};
  people.forEach((p, i) => {
    const id = trim(p.idNumber).replace(/\s+/g, '');
    if (!id) return;
    if (seen[id] !== undefined) {
      e[`people.${i}.idNumber`] = `This ID number is already used for person ${seen[id] + 1}.`;
    } else {
      seen[id] = i;
    }
  });

  const s = shareholdingSummary(people);
  if (s.shareholderCount > 0) {
    if (Math.abs(s.totalPct - 100) > 0.01) {
      e['people.shareholdingTotal'] = `Shareholding must add up to 100% (currently ${formatPct(s.totalPct)}%).`;
    }
    if (isPositiveInt(authorisedShares) && s.totalShares > authorisedShares) {
      e['people.sharesTotal'] = `The shares issued (${s.totalShares}) cannot be more than the authorised shares (${authorisedShares}).`;
    }
  }
  return e;
}

export function validateNameStep(name: RegistrationName): FieldErrors {
  const e: FieldErrors = {};
  if (name.hasReservation !== 'yes' && name.hasReservation !== 'no') {
    e['name.hasReservation'] = 'Tell us whether a name is reserved at CIPC.';
    return e;
  }
  if (name.hasReservation === 'yes') {
    const n = trim(name.reservedName);
    if (!n) e['name.reservedName'] = 'Enter the reserved company name.';
    else if (tooLong(n, 160)) e['name.reservedName'] = 'Keep the name under 160 characters.';
    const ref = trim(name.reservationReference);
    if (!ref) e['name.reservationReference'] = 'Enter the reservation reference or tracking number.';
    else if (tooLong(ref, 80)) e['name.reservationReference'] = 'Keep the reference under 80 characters.';
    return e;
  }
  const opts = Array.isArray(name.options) ? name.options : [];
  const seen: Record<string, number> = {};
  for (let i = 0; i < 4; i++) {
    const v = trim(opts[i]);
    if (!v) {
      e[`name.options.${i}`] = `Enter name option ${i + 1}.`;
      continue;
    }
    if (tooLong(v, 160)) {
      e[`name.options.${i}`] = 'Keep the name under 160 characters.';
      continue;
    }
    const k = v.toLowerCase().replace(/\s+/g, ' ');
    if (seen[k] !== undefined) e[`name.options.${i}`] = `This is the same as option ${seen[k] + 1}. Each option must be different.`;
    else seen[k] = i;
  }
  return e;
}

export function validateDeclaration(d: RegistrationDeclaration): FieldErrors {
  const e: FieldErrors = {};
  if (d.accepted !== true) e['declaration.accepted'] = 'Tick the declaration to continue.';
  const n = trim(d.fullName);
  if (!n) e['declaration.fullName'] = 'Enter your full name.';
  else if (tooLong(n, 120)) e['declaration.fullName'] = 'Keep the name under 120 characters.';
  if (!trim(d.signature)) e['declaration.signature'] = 'Sign the declaration.';
  return e;
}

/** Everything — what the function runs before accepting a submission. */
export function validateRegistration(p: RegistrationPayload, opts: PersonValidationOptions = {}): FieldErrors {
  if (!p || typeof p !== 'object') return { form: 'The submission is empty.' };
  const company = p.company || ({} as RegistrationCompany);
  return {
    ...validateCompanyStep(company),
    ...validatePeopleStep(p.people || [], company.authorisedShares ?? null, opts),
    ...validateNameStep(p.name || ({} as RegistrationName)),
    ...validateDeclaration(p.declaration || ({} as RegistrationDeclaration)),
  };
}

// ── Display helpers (form review step, PDFs, emails) ─────────────────────

export function formatPct(v: number): string {
  return (Math.round(v * 100) / 100).toFixed(2).replace(/\.00$/, '');
}

/** The company's display name: reserved name, else option 1. */
export function companyDisplayName(name: RegistrationName): string {
  if (name.hasReservation === 'yes' && trim(name.reservedName)) return trim(name.reservedName);
  return trim((name.options || [])[0]) || 'New company';
}

export function rolesLabel(r: PersonRoles): string {
  const out: string[] = [];
  if (r.director) out.push('Director');
  if (r.shareholder) out.push('Shareholder');
  if (r.incorporator) out.push('Incorporator');
  return out.join(', ');
}

/** For file names: last word = surname, the rest become initials. */
export function surnameAndInitials(fullName: string): { surname: string; initials: string } {
  const parts = trim(fullName).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { surname: 'Person', initials: '' };
  const surname = parts[parts.length - 1];
  const initials = parts.slice(0, -1).map((w) => w[0].toUpperCase()).join('');
  return { surname, initials };
}

/**
 * Town guess for the POA "Signed at" prefill: the last line / comma part of
 * the business address, without a postcode. "12 Main Rd, Bellville, 7530" →
 * "Bellville". Only a prefill — the field stays editable.
 */
export function townFromAddress(address: string): string {
  const parts = trim(address)
    .split(/[\n,]+/)
    .map((l) => l.replace(/\b\d{4}\b/g, '').trim())
    .filter((l) => l && !/^\d/.test(l));
  return parts.length ? parts[parts.length - 1] : '';
}
